<template>
  <Panel
    :id="id"
    class="bill-to elevated"
    :aria-label="t('common.billTos')"
    role="region"
    tabindex="-1"
  >
    <template #header>
      <div class="bill-to-header flex">
        <LabeledIcon
          class="bill-to-header-label"
          icon="pi pi-map-marker"
          :label="t('common.billTos')"
        >
          <template #label="{ label }">
            <span class="w-max" v-text="label" />
          </template>
        </LabeledIcon>

        <div
          class="bill-to-header-address flex flex-gap-1 justify-content-between align-items-center w-full"
        >
          <div class="flex flex-gap-1 align-items-center">
            <span class="bill-to-location-name">
              {{
                internalValue.location?.locationId
                  ? internalValue.location?.locationName
                  : t('common.newParam', { param: t('common.billTos') })
              }}
            </span>

            <template
              v-if="
                internalValue.location.addressLine1 ||
                internalValue.location.cityStatePostalCode
              "
            >
              <span>-</span>

              <span v-if="internalValue.location.addressLine1">
                {{ internalValue.location.addressLine1 }}
              </span>

              <span v-if="internalValue.location.cityStatePostalCode">
                {{ internalValue.location.cityStatePostalCode }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #icons>
      <RygenButton
        v-if="internalValue.location?.locationId || v$.$anyDirty"
        mode="tertiary"
        :label="t('orders.clearAndStartOver')"
        :disabled="disabled"
        :data-testid="`${id}-clear-button`"
        @click="onClear"
      />

      <RygenButton
        v-if="!disabled"
        mode="tertiary"
        :label="
          internalValue.location.locationId
            ? t('common.changes')
            : t('address.addressBook')
        "
        @click="isShowingAddressBook = true"
      />

      <ExpandButton
        v-model="isExpanded"
        :target-id="`${id}-content`"
        :data-testid="`${id}-expand-button`"
        no-labels
      />
    </template>

    <template #default>
      <BillToEditContent
        :id="`${id}-content`"
        v-model="internalValue"
        v-model:expanded="isExpanded"
        @address-book-select="onBillToSelect"
        @autocomplete="onAutocomplete"
        @clear="onClear"
      />

      <AddressBookModal
        v-model:visible="isShowingAddressBook"
        v-model:search-criteria="searchCriteria"
        type="billTo"
        :header="t('common.addParam', { param: t('common.billTos') })"
        :search-results="searchResults"
        data-testid="bill-to-edit-address-book-modal"
        @select="onBillToSelect"
      />
    </template>
  </Panel>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { BillToDetails, Location } from '@rygen/rygen-ui/model';

import { useBillTos } from '@/composables';
import { BillTo, EmbeddableLocation, OrderBillTo } from '@/model';

import BillToEditContent from '@/components/location/BillToEditContent.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import AddressBookModal from '@rygen/rygen-ui/components/address/AddressBookModal.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Panel from 'primevue/panel';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: Object as PropType<BillTo>,
    default: () => new OrderBillTo(),
  },
  id: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'update:model-value', modelValue: BillTo): void;
}>();

const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
const internalValue: Ref<BillTo> = ref(new Clazz(modelValue.value));

const { resetSearch, searchCriteria, searchResults } = useBillTos();
const { t } = useI18n();
const { v$ } = useValidations(internalValue, {
  billToDetails: {
    invoicePayingParty: {
      required: required(t('common.invoicePayingParties')),
    },
  },
});

const isShowingAddressBook = ref(false);

const onClear = () => {
  internalValue.value = new Clazz({
    id: internalValue.value?.id,
    location: new EmbeddableLocation(),
  });
  nextTick(() => v$.value.$reset());
};

const onBillToSelect = (location: Location) => {
  if (!location.id) {
    return;
  }

  internalValue.value.location = EmbeddableLocation.fromLocation(location);
  internalValue.value.billToDetails = new BillToDetails(location.billToDetails);
};

const onAutocomplete = (isAutocompleteSelection: boolean) => {
  internalValue.value.location.hasBeenValidated = isAutocompleteSelection;
  emit('autocomplete', isAutocompleteSelection);
};

watch(isShowingAddressBook, (_newVal: boolean) => {
  resetSearch();
});

watch(
  modelValue,
  (newVal: BillTo) => {
    const Clazz = Object.getPrototypeOf(newVal).constructor;
    internalValue.value = new Clazz(newVal);
    v$.value.$reset();
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: BillTo) => {
    if (!isEqual(modelValue.value, newVal)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
.bill-to.p-panel {
  --p-panel-border-radius: var(--p-border-radius-sm);
  --p-panel-header-background: white;
  --p-panel-header-border-width: 0 0 1px var(--sp-1);
  --p-panel-header-border-color: transparent transparent var(--divider-color)
    var(--stop-type-accent-color);
  --p-panel-header-border-radius: var(--p-border-radius-sm)
    var(--p-border-radius-sm) 0 0;
  --p-panel-header-color: var(--p-gray-600);
  --p-panel-header-padding: 0 var(--sp-6) 0 0;
  --p-panel-content-padding: var(--sp-3) 0;

  .p-panel-header {
    font-size: 16px;
    font-weight: 500;

    .bill-to-header {
      flex: 1 1 auto;
      min-width: 0;

      .bill-to-header-label {
        border-right: var(--divider);
        padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-6);
        width: 200px;

        .pi {
          color: var(--bill-to-color);
        }
      }

      .bill-to-header-address {
        padding: var(--sp-3);

        .bill-to-location-name {
          font-weight: normal;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: var(--panel-header-text-max-width);
          min-width: 0;
        }
      }
    }
  }

  .p-panel-content {
    .grid {
      padding: 0 var(--sp-6);
    }
  }
}
</style>
